import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

/**
 * Importador de ACCESORIOS y GARANTÍAS por agencia desde el machote de Excel.
 *
 * - La agencia, la marca y el tipo (accesorios / garantias) se eligen en
 *   pantalla; no van en el Excel. La marca se preselecciona según el nombre
 *   de la agencia ("Nissan Santa Clara" -> Nissan; "Gocar Seminuevos" -> all).
 * - Lee el .xlsx en el navegador (SheetJS) y reconoce columnas por nombre.
 * - Valida cada fila y simula el filtro de modelo/versión de customer-order-v2
 *   (crearFiltroModeloAccesorios, copiado tal cual) en cada auto del catálogo
 *   real de babilonia_nuevos, para mostrar en qué modelos / versiones aparecerá.
 * - Antes de guardar pide al servidor un dry_run con el resumen.
 */

export const MACHOTES = {
  accesorios: "/machotes/machote_accesorios_babilonia.xlsx",
  garantias: "/machotes/machote_garantias_babilonia.xlsx",
};

const TIPOS = {
  accesorios: { label: "Accesorios", singular: "accesorio" },
  garantias: { label: "Garantías", singular: "garantía" },
};

// ---------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------
const normalizarTexto = (texto = "") =>
  String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapeRegExp = (texto = "") =>
  String(texto).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const incluyePalabra = (texto = "", palabra = "") => {
  const t = normalizarTexto(texto);
  const p = normalizarTexto(palabra);
  if (!t || !p) return false;
  return new RegExp(`\\b${escapeRegExp(p)}\\b`, "i").test(t);
};

// =====================================================================
// FILTRO DE MODELO / VERSIÓN — COPIA TEXTUAL de customer-order-v2.js
// (bloque de ALIAS_TOKENS_MODELO a crearFiltroModeloAccesorios).
// Si se cambia allá, copiarlo de nuevo aquí para que la vista previa
// muestre exactamente lo que verá el cliente.
// ---------------------------------------------------------------------
// Convención en BD: "PRODUCTO - MODELO1, MODELO2"  (sin " - " = aplica a todos)
// Cada modelo se convierte a una "llave" compacta, así da igual cómo se escriba:
//   "X-Trail", "X TRAIL", "XTRAIL", "x-trail 2026"  -> "xtrail"
//   "Kicks Play", "KICKSPLAY"                       -> "kicksplay"
//   "X-Trail e-POWER", "X-Trail Hybrid"             -> "xtrailhibrido"
//   "Frontier PR04X", "FRONTIER PRO-4X"             -> "frontierpro4x"
//   "MG 5", "MG5"                                   -> "mg5"
// En garantías se puede listar por versión: "LOCALIZADOR 1 AÑO - Frontier LE, March Exclusive"
// =====================================================================
const ALIAS_TOKENS_MODELO = {
  epower: "hibrido",
  hybrid: "hibrido",
  hibrido: "hibrido",
  hev: "hibrido",
  ehev: "hibrido", // Honda "e:HEV"
  pr04x: "pro4x",
};

const MARCAS_CONOCIDAS_MODELO = [
  "nissan", "renault", "honda", "mg", "seat", "cupra", "volkswagen", "vw", "chirey",
];

const normalizarTextoModelo = (texto = "") =>
  String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\-_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const distanciaModelo = (a = "", b = "") => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const curr = [i];
    for (let j = 1; j <= b.length; j += 1) {
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = curr;
  }
  return prev[b.length];
};

// Modelo -> tokens limpios (sin años, sin marca, con alias)
const tokensModelo = (texto = "", marca = "") => {
  const marcasQuitar = new Set([
    ...MARCAS_CONOCIDAS_MODELO,
    ...normalizarTextoModelo(marca).split(" ").filter(Boolean),
  ]);

  const crudos = normalizarTextoModelo(texto)
    .replace(/\be\s+power\b/g, "epower") // "e-POWER" -> "e power" -> "epower"
    .split(" ")
    .map((t) => t.replace(/[^a-z0-9]/g, ""))
    .filter(Boolean)
    .filter((t) => !/^(20\d{2}|2[4-9])$/.test(t)); // años: 2026, 26...

  const tokens = [];
  for (let i = 0; i < crudos.length; i += 1) {
    const t = crudos[i];
    if (marcasQuitar.has(t)) {
      // "MG 5" -> "mg5": si a la marca le sigue solo un número, es parte del modelo
      if (/^\d+$/.test(crudos[i + 1] || "")) {
        tokens.push(t + crudos[i + 1]);
        i += 1;
      }
      continue;
    }
    tokens.push(ALIAS_TOKENS_MODELO[t] || t);
  }
  return tokens;
};

const llaveModelo = (texto = "", marca = "") => tokensModelo(texto, marca).join("");

// Llaves de modelo del título "Nombre - Mod1, Mod2". null = sin lista (aplica a todos)
const llavesModeloDeItem = (item, marca = "") => {
  const title = String(item?.title || "");
  if (!title.includes(" - ")) return null;
  const [, rawModelos = ""] = title.split(" - ");
  return rawModelos
    .split(",")
    .map((m) => llaveModelo(m, marca))
    .filter(Boolean);
};

const soloDigitosModelo = (texto = "") => String(texto).replace(/\D/g, "");

// Busca en el catálogo la llave que corresponde a lo capturado. "" si no hay.
// 1) exacta  1b) lo capturado es el inicio de un solo modelo ("kicksplay" -> "kicksplayhibrido")
// 2) lo capturado EMPIEZA con la llave (gana la más larga: "kicksplay" > "kicks")
// 3) la llave aparece completa dentro de lo capturado ("Golf GTI" -> "gti")
// 4) error de dedo (Levenshtein) solo si los números coinciden ("Tiggo 9" ≠ "Tiggo 7")
const buscarModeloCatalogo = (capturado = "", llavesCatalogo = [], marca = "") => {
  const tokens = tokensModelo(capturado, marca);
  const llave = tokens.join("");
  if (!llave) return "";

  const catalogo = Array.from(new Set(llavesCatalogo)).filter(
    (k) => k && k !== "todos",
  );
  if (catalogo.includes(llave)) return llave;

  const masLarga = (lista) => lista.sort((x, y) => y.length - x.length)[0];

  // Lo capturado es el inicio de UN solo modelo del catálogo:
  // "KICKSPLAY" -> "Kicks Play e-POWER", "Frontier V6" -> "Frontier V6 Pro-4X"
  const porExtension = catalogo.filter((k) => k.startsWith(llave));
  if (llave.length >= 4 && porExtension.length === 1) return porExtension[0];

  const porPrefijo = catalogo.filter((k) => k.length >= 2 && llave.startsWith(k));
  if (porPrefijo.length) return masLarga(porPrefijo);

  const segmentos = new Set();
  for (let i = 0; i < tokens.length; i += 1) {
    for (let j = i + 1; j <= tokens.length; j += 1) {
      segmentos.add(tokens.slice(i, j).join(""));
    }
  }
  const porSegmento = catalogo.filter((k) => k.length >= 2 && segmentos.has(k));
  if (porSegmento.length) return masLarga(porSegmento);

  if (llave.length >= 5) {
    const tolerancia = llave.length >= 6 ? 2 : 1;
    const porDistancia = catalogo
      .filter((k) => soloDigitosModelo(k) === soloDigitosModelo(llave))
      .map((k) => ({ k, d: distanciaModelo(k, llave) }))
      .filter(({ d }) => d <= tolerancia)
      .sort((x, y) => x.d - y.d || y.k.length - x.k.length);
    if (porDistancia.length) return porDistancia[0].k;
  }
  return "";
};

// Une modelo + versión sin repetir el modelo cuando la versión ya lo trae:
//   "Frontier" + "Frontier Se TM" -> "frontier se tm"   (no "frontier frontier se tm")
//   "NP300" + "Np300 Pick Up TM Ac" -> "np300 pick up tm ac"
const textoModeloVersion = (modelo = "", version = "", marca = "") => {
  const tm = tokensModelo(modelo, marca);
  let tv = tokensModelo(version, marca);
  const llaveM = tm.join("");

  if (llaveM) {
    let acumulado = "";
    for (let i = 0; i < tv.length; i += 1) {
      acumulado += tv[i];
      if (acumulado === llaveM) {
        tv = tv.slice(i + 1);
        break;
      }
      if (!llaveM.startsWith(acumulado)) break;
    }
  }

  return [...tm, ...tv].join(" ");
};

const esItemGarantia = (item) =>
  normalizarTextoModelo(item?.type || item?.tipo || "") === "garantias";

// Crea el filtro de modelo/versión para un auto.
// items: catálogo YA filtrado por marca y agencia (accesorios + garantías),
//        idealmente junto con los vehículos nuevos (type "nuevos").
// Regresa (item) => boolean.
//  - Accesorios: por modelo. Si la versión trae el sub-modelo (p. ej. "e-POWER",
//    "PRO-4X", "Play") se toma en cuenta: modelo "X-Trail" + versión "e-POWER" -> "xtrailhibrido".
//  - Garantías: por modelo o por modelo+versión ("Frontier LE"). Si no hay versión
//    capturada, se muestran también las garantías por versión de ese modelo.
const crearFiltroModeloAccesorios = (
  items = [],
  { marca = "", modelo = "", version = "" } = {},
) => {
  const llavesAcc = [];
  const llavesGar = [];
  const llavesModelos = []; // modelos reales del catálogo de nuevos
  (Array.isArray(items) ? items : []).forEach((item) => {
    // Vehículos nuevos (babilonia_nuevos): el nombre del modelo viene en
    // "description". Sirven para distinguir modelos que empiezan igual:
    // "Kicks Play e-POWER" ≠ "Kicks", "X-Trail e-POWER" ≠ "X-Trail",
    // "Frontier V6 Pro-4X" ≠ "Frontier".
    if (normalizarTextoModelo(item?.type || "") === "nuevos") {
      const llave = llaveModelo(item?.description || "", marca);
      if (llave) llavesModelos.push(llave);
      return;
    }
    const lista = llavesModeloDeItem(item, marca);
    if (!lista) return;
    (esItemGarantia(item) ? llavesGar : llavesAcc).push(...lista);
  });

  const catalogoBase = [
    ...(llavesAcc.length ? llavesAcc : llavesGar),
    ...llavesModelos,
  ];
  const modeloVersionTxt = textoModeloVersion(modelo, version, marca);
  const sinVersion = !normalizarTextoModelo(version);

  // Si el modelo capturado ES exactamente un modelo conocido (en accesorios o
  // garantías), se respeta: "Kicks Play e-POWER" no cae en "Kicks" ni
  // "Frontier V6 Pro-4X" en "Frontier". Solo se usa la versión si da un
  // sub-modelo más específico del mismo ("Frontier" + "Pro-4X" -> "frontierpro4x").
  const llaveSoloModelo = llaveModelo(modelo, marca);
  const modeloExacto = [...llavesAcc, ...llavesGar].includes(llaveSoloModelo)
    ? llaveSoloModelo
    : "";
  const porVersion = buscarModeloCatalogo(modeloVersionTxt, catalogoBase, marca);

  const modeloBase =
    (porVersion &&
    (!modeloExacto ||
      (porVersion.length > modeloExacto.length &&
        porVersion.startsWith(modeloExacto)))
      ? porVersion
      : "") ||
    modeloExacto ||
    buscarModeloCatalogo(modelo, catalogoBase, marca) ||
    llaveSoloModelo;

  const candidatosGarantia = new Set(
    [
      modeloBase,
      buscarModeloCatalogo(modelo, llavesGar, marca),
      buscarModeloCatalogo(modeloVersionTxt, llavesGar, marca),
    ].filter(Boolean),
  );

  const catalogoSinVersion = [...catalogoBase, modeloBase];

  return (item) => {
    const lista = llavesModeloDeItem(item, marca);
    if (!lista) return true; // sin lista de modelos: aplica a todos
    if (lista.includes("todos")) return true;
    if (!modeloBase) return false;

    if (!esItemGarantia(item)) return lista.includes(modeloBase);

    if (lista.some((k) => candidatosGarantia.has(k))) return true;

    // Sin versión capturada: "Frontier LE" cuenta como "Frontier",
    // pero "Kicks Play" NO cuenta como "Kicks".
    if (sinVersion) {
      return lista.some(
        (k) =>
          k.startsWith(modeloBase) &&
          buscarModeloCatalogo(k, catalogoSinVersion) === modeloBase,
      );
    }
    return false;
  };
};
// =====================================================================

// ---------------------------------------------------------------------
// Lectura del Excel
// ---------------------------------------------------------------------
const normalizarHeader = (h = "") =>
  normalizarTexto(
    String(h)
      .replace(/\*/g, "")
      .replace(/\(.*?\)/g, "")
      .replace(/\./g, ""),
  );

const HEADER_ALIASES = {
  no_pieza: [
    "no de pieza", "no pieza", "clave no de pieza", "clave", "codigo",
    "numero de pieza", "num pieza", "pieza", "sku",
  ],
  producto: ["producto", "accesorio", "garantia", "nombre"],
  aplica_a: ["aplica a", "modelo que aplica", "vehiculo", "modelo", "aplicacion"],
  precio: [
    "precio publico c iva", "precio publico con iva", "precio c iva",
    "precio con iva", "precio publico", "precio",
  ],
  tipo_compra: ["tipo de compra", "tipo compra"],
  image: ["url imagen", "imagen"],
  video: ["url video", "video"],
  pdf: ["url pdf", "pdf", "ficha tecnica"],
};

const REQUERIDAS = ["no_pieza", "producto", "aplica_a", "precio"];

const mapearColumnas = (headerRow = []) => {
  const normalizados = headerRow.map(normalizarHeader);
  const mapa = {};
  Object.entries(HEADER_ALIASES).forEach(([campo, aliases]) => {
    for (const alias of aliases) {
      const idx = normalizados.indexOf(alias);
      if (idx !== -1 && !Object.values(mapa).includes(idx)) {
        mapa[campo] = idx;
        break;
      }
    }
  });
  return mapa;
};

const parsePrecio = (valor) => {
  if (typeof valor === "number") return Math.round(valor * 100) / 100;
  const n = parseFloat(String(valor ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

const limpiar = (v) => String(v ?? "").replace(/\s+/g, " ").trim();

// "www.nissan.com.mx/x.jpg" -> "https://www.nissan.com.mx/x.jpg"
const normalizarUrl = (v) => {
  const u = limpiar(v).replace(/\s/g, "");
  if (!u) return "";
  return /^www\./i.test(u) ? `https://${u}` : u;
};

const leerExcel = async (file) => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });

  // Si existe la hoja del machote (ACCESORIOS / GARANTIAS) solo se lee esa,
  // para no tomar los ejemplos de INSTRUCCIONES.
  const hojasMachote = wb.SheetNames.filter((n) =>
    ["accesorios", "garantias"].includes(normalizarTexto(n)),
  );
  const nombres = hojasMachote.length ? hojasMachote : wb.SheetNames;

  for (const nombre of nombres) {
    const matriz = XLSX.utils.sheet_to_json(wb.Sheets[nombre], {
      header: 1,
      raw: true,
      defval: "",
      blankrows: false,
    });

    for (let i = 0; i < Math.min(matriz.length, 15); i++) {
      const mapa = mapearColumnas(matriz[i]);
      if (!REQUERIDAS.every((c) => mapa[c] !== undefined)) continue;

      const filas = [];
      for (let r = i + 1; r < matriz.length; r++) {
        const row = matriz[r];
        const get = (campo) => (mapa[campo] === undefined ? "" : row[mapa[campo]]);

        const registro = {
          fila: r + 1,
          no_pieza: limpiar(get("no_pieza")).toUpperCase(),
          producto: limpiar(get("producto")),
          aplica_a: limpiar(get("aplica_a")),
          precio: parsePrecio(get("precio")),
          tipo_compra: limpiar(get("tipo_compra")) || "CREDITO Y CONTADO",
          image: normalizarUrl(get("image")),
          video: normalizarUrl(get("video")),
          pdf: normalizarUrl(get("pdf")),
        };

        if (registro.no_pieza || registro.producto || registro.aplica_a) {
          filas.push(registro);
        }
      }
      return { hoja: nombre, filas };
    }
  }

  throw new Error(
    "No encontré las columnas No. de pieza, Producto, Aplica a y Precio. Usa el machote.",
  );
};

// ---------------------------------------------------------------------
// Catálogo de nuevos: modelos y versiones (babilonia_nuevos)
// ---------------------------------------------------------------------
const parseVersiones = (raw) => {
  let v = raw;
  try {
    if (typeof v === "string") v = JSON.parse(v);
  } catch {
    return [];
  }
  if (v && !Array.isArray(v) && v.version) v = [v];
  return Array.isArray(v) ? v.map((x) => limpiar(x?.version)).filter(Boolean) : [];
};

const construirCatalogo = (items = []) => {
  const modelos = new Map(); // norm -> { label, versiones:Set }
  items.forEach((it) => {
    const label = limpiar(it?.modelo);
    const norm = normalizarTexto(label);
    if (!norm) return;
    if (!modelos.has(norm)) modelos.set(norm, { label, versiones: new Set() });
    parseVersiones(it?.versiones).forEach((v) => modelos.get(norm).versiones.add(v));
  });

  const lista = [...modelos.values()]
    .map((m) => ({ ...m, versiones: [...m.versiones].sort() }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Cada auto nuevo que se va a simular: modelo + versión.
  const vehiculos = [];
  lista.forEach((m) => {
    (m.versiones.length ? m.versiones : [""]).forEach((version) =>
      vehiculos.push({ modelo: m.label, version }),
    );
  });

  return { modelos: lista, vehiculos };
};

// ---------------------------------------------------------------------
// Formato de "Aplica a": "MODELO1, MODELO2" o "TODOS".
// El filtro compara "llaves" (sin espacios, guiones ni años), así que aquí
// solo se limpia lo que rompería la comparación:
//   - "TODOS LOS MODELOS NISSAN" / "SOLO MODELOS NISSAN"  -> "TODOS"
//     (el filtro solo reconoce la palabra TODOS sola)
//   - años y rangos ("2020-2025"), "NEW", "VERSIONES", "6 CIL"
//   - "/" o ";" como separador -> ", "
// Las versiones se conservan: el filtro nuevo sí las distingue
// ("FRONTIER PRO-4X", "KICKS PLAY", "X-TRAIL E-POWER").
// ---------------------------------------------------------------------
const PALABRAS_RUIDO = ["new", "nuevo", "nueva", "version", "versiones", "modelo", "modelos"];

const limpiarSegmento = (texto = "") =>
  String(texto)
    .toUpperCase()
    .replace(/\b(19|20)\d{2}\s*[-–]\s*(19|20)\d{2}\b/g, " ")
    .replace(/\b(19|20)\d{2}\b/g, " ")
    .replace(/\b\d+\s*CIL(INDROS)?\b/g, " ")
    .split(/\s+/)
    .filter((w) => w && !PALABRAS_RUIDO.includes(normalizarTexto(w)))
    .join(" ")
    .trim();

const partirSegmentos = (original) =>
  String(original)
    .split(/[/,;|]+/)
    .map((x) => x.trim())
    .filter(Boolean);

const esSegmentoUniversal = (seg, brand) => {
  const n = normalizarTexto(seg);
  const marca = normalizarTexto(brand);
  return (
    n.includes("todos") ||
    n.includes("todas") ||
    /^solo modelos\b/.test(n) ||
    (marca && marca !== "all" && (n === marca || n === `solo ${marca}`))
  );
};

const canonicalizarAplica = (original, brand) => {
  const avisos = [];
  const segmentos = partirSegmentos(original);

  if (segmentos.some((x) => esSegmentoUniversal(x, brand))) {
    if (segmentos.length > 1) avisos.push(`Se dejó "TODOS" (el Excel decía "${original}").`);
    return { valor: "TODOS", avisos };
  }

  const salida = segmentos.map(limpiarSegmento).filter(Boolean);
  return { valor: [...new Set(salida)].join(", "), avisos };
};

// ---------------------------------------------------------------------
// URLs: opcionales. La instrucción del machote pide imagen del sitio de la
// marca, pero si viene vacía se importa igual.
// ---------------------------------------------------------------------
const DOMINIOS_NO_RECOMENDADOS = [
  "google.", "gstatic.", "bing.", "mercadolibre", "mlstatic", "amazon.", "media-amazon",
  "ssl-images-amazon", "aliexpress", "alicdn", "temu.", "shein.", "ebay.", "ebayimg",
  "walmart.", "pinterest.", "pinimg.", "facebook.", "fbcdn.", "instagram.", "cdninstagram.",
];

const DOMINIOS_MARCA = {
  volkswagen: ["volkswagen", "vw."],
  chirey: ["chirey", "chery"],
};

const hostDe = (url = "") => {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
};

const validarUrl = (url, etiqueta, brand = "") => {
  const errores = [];
  const avisos = [];
  if (!url) return { errores, avisos };

  const host = hostDe(url);
  if (!/^https?:\/\//i.test(url) || !host) {
    errores.push(`${etiqueta}: "${url}" no es una liga válida (debe empezar con https://). Corrígela o déjala vacía.`);
    return { errores, avisos };
  }
  if (url.length > 255) {
    errores.push(`${etiqueta}: la liga es muy larga (máx. 255 caracteres).`);
  }
  if (/^http:\/\//i.test(url)) {
    avisos.push(`${etiqueta}: usa https:// (algunos navegadores bloquean http://).`);
  }
  if (DOMINIOS_NO_RECOMENDADOS.some((d) => host.includes(d))) {
    avisos.push(`${etiqueta}: es de ${host}; debe ser del sitio oficial de la marca.`);
    return { errores, avisos };
  }

  const marca = normalizarTexto(brand).replace(/\s+/g, "");
  if (marca && marca !== "all") {
    const esperados = DOMINIOS_MARCA[marca] || [marca];
    if (!esperados.some((d) => host.includes(d))) {
      avisos.push(`${etiqueta}: ${host} no parece ser el sitio de ${brand}.`);
    }
  }
  return { errores, avisos };
};

// ---------------------------------------------------------------------
// Validación por fila + simulación del filtro en cada auto nuevo
// ---------------------------------------------------------------------
// Sugerencias para una parte de "Aplica a" que no coincide con ningún auto
// nuevo: busca en el catálogo modelos parecidos usando las mismas llaves y
// la misma distancia que usa el filtro de customer-order-v2.
//   "KICKS E-POWER" -> KICKS PLAY E-POWER   (le faltaba una palabra)
//   "SENTR"         -> SENTRA               (error de dedo)
//   "XE"            -> (sin sugerencia)
const sugerirModelos = (seg, catalogo, marca = "") => {
  const tSeg = tokensModelo(seg, marca);
  if (!tSeg.length) return [];
  const kSeg = tSeg.join("");
  const enSeg = new Set(tSeg);

  const candidatos = catalogo.modelos
    .map((m) => {
      const tMod = tokensModelo(m.label, marca);
      const kMod = tMod.join("");
      if (!kMod) return null;

      const comunes = tMod.filter((t) => enSeg.has(t)).length;
      const dist = distanciaModelo(kSeg, kMod);
      const tolerancia = Math.max(1, Math.floor(Math.max(kSeg.length, kMod.length) * 0.25));
      const parecido = dist <= tolerancia;
      if (!comunes && !parecido) return null;

      let puntos = comunes * 2;
      if (tSeg.every((t) => tMod.includes(t))) puntos += 4; // al Excel le faltan palabras
      if (tMod.every((t) => enSeg.has(t))) puntos += 1; // al Excel le sobran palabras
      if (kMod.startsWith(kSeg) || kSeg.startsWith(kMod)) puntos += 1;
      if (parecido) puntos += 2;

      return { valor: m.label.toUpperCase(), puntos, dist };
    })
    .filter((c) => c && c.puntos >= 3)
    .sort((a, b) => b.puntos - a.puntos || a.dist - b.dist);

  if (!candidatos.length) return [];
  // La mejor y, si queda muy cerca, una segunda opción.
  return candidatos
    .filter((c, i) => i === 0 || (i === 1 && candidatos[0].puntos - c.puntos <= 1))
    .map((c) => c.valor);
};

// Agrupa los autos donde aparece: "March" (todas sus versiones) o
// "Frontier (Pro-4X, LE)" si solo algunas.
const agruparVehiculos = (vehiculos, catalogo) => {
  const porModelo = new Map();
  vehiculos.forEach(({ modelo, version }) => {
    if (!porModelo.has(modelo)) porModelo.set(modelo, []);
    porModelo.get(modelo).push(version);
  });
  const salida = [];
  catalogo.modelos.forEach((m) => {
    const ven = porModelo.get(m.label);
    if (!ven) return;
    const total = m.versiones.length || 1;
    if (ven.length === total) salida.push(m.label);
    else salida.push(`${m.label} (${ven.filter(Boolean).join(", ")})`);
  });
  return salida;
};

const validarFilas = (filas, catalogo, brand, tipo) => {
  const titulos = {};
  const esGarantia = tipo === "garantias";
  const marcaFiltro = normalizarTexto(brand) === "all" ? "" : brand;

  // 1) Normalizar y validar campos
  const base = filas.map((f) => {
    const errores = [];
    const avisos = [];

    const aplicaOriginal = f.aplica_original ?? f.aplica_a;
    const { valor: aplica, avisos: avisosAplica } = canonicalizarAplica(aplicaOriginal, brand);
    avisos.push(...avisosAplica);

    const title = `${f.producto} - ${aplica}`;

    if (!f.no_pieza) errores.push(esGarantia ? "Falta Clave" : "Falta No. de pieza");
    if (!f.producto) errores.push("Falta Producto");
    if (!aplica) errores.push('Falta "Aplica a"');
    if (!(f.precio > 0)) errores.push("Precio inválido");
    if (/\s-\s/.test(f.producto)) {
      errores.push('El producto no puede llevar " - " (el cliente vería el nombre cortado)');
    }

    [
      validarUrl(f.image, "URL imagen", brand),
      validarUrl(f.video, "URL video"),
      validarUrl(f.pdf, "URL PDF", brand),
    ].forEach((r) => {
      errores.push(...r.errores);
      avisos.push(...r.avisos);
    });

    if (f.producto && aplica) {
      const k = title.toLowerCase();
      if (titulos[k]) errores.push(`Producto + modelo repetido (fila ${titulos[k]})`);
      else titulos[k] = f.fila;
    }

    return { ...f, aplica_original: aplicaOriginal, aplica_a: aplica, title, errores, avisos };
  });

  // 2) Simular el filtro de customer-order-v2 en cada auto nuevo del catálogo.
  //    El catálogo del filtro = lo que se va a importar + los modelos de nuevos.
  const items = base
    .filter((r) => r.producto && r.aplica_a)
    .map((r) => ({ title: r.title, type: tipo }));
  catalogo.modelos.forEach((m) => items.push({ type: "nuevos", description: m.label }));

  const filtros = catalogo.vehiculos.map((v) => ({
    ...v,
    pasa: crearFiltroModeloAccesorios(items, {
      marca: marcaFiltro,
      modelo: v.modelo,
      version: v.version,
    }),
  }));

  return base.map((r) => {
    const avisos = [...r.avisos];
    let aplicaA = [];
    let sugerencia = "";

    if (r.aplica_a && filtros.length) {
      const item = { title: r.title, type: tipo };
      const lista = llavesModeloDeItem(item, marcaFiltro) || [];

      if (lista.includes("todos")) {
        aplicaA = ["TODOS"];
      } else {
        aplicaA = agruparVehiculos(filtros.filter((f) => f.pasa(item)), catalogo);

        // Partes de "Aplica a" que no le salen a ningún auto nuevo
        const sinMatch = partirSegmentos(r.aplica_a).filter((seg) => {
          const prueba = { title: `X - ${seg}`, type: tipo };
          return !filtros.some((f) => f.pasa(prueba));
        });

        if (sinMatch.length) {
          const sugerencias = {};
          sinMatch.forEach((seg) => {
            sugerencias[seg] = sugerirModelos(seg, catalogo, marcaFiltro);
          });

          sinMatch.forEach((seg) => {
            const opciones = sugerencias[seg];
            avisos.push(
              `"${seg}" no coincide con ningún modelo / versión del catálogo de nuevos` +
                (opciones.length ? `. ¿Quisiste decir ${opciones.join(" o ")}?` : "."),
            );
          });

          if (!aplicaA.length) {
            avisos.push("Así como está, no aparecerá en autos nuevos.");
          }

          // "Aplica a" completo ya corregido, para copiarlo al Excel.
          const corregido = [
            ...new Set(
              partirSegmentos(r.aplica_a)
                .map((seg) => (sinMatch.includes(seg) ? sugerencias[seg][0] : seg))
                .filter(Boolean),
            ),
          ].join(", ");
          if (corregido && corregido !== r.aplica_a) sugerencia = corregido;
        }
      }
    }

    // Reglas de visibilidad por nombre de customer-order-v2, solo informativas.
    const tituloNorm = normalizarTexto(r.title);
    if (tituloNorm.includes("kit de seguridad") || tituloNorm.includes("localizador gocar")) {
      avisos.push("Por su nombre, solo se muestra en seminuevos.");
    }
    if (esGarantia && tituloNorm.includes("garantia mecanica")) {
      avisos.push('"Garantía mecánica" no se muestra en autos nuevos.');
    }

    return {
      ...r,
      aplicaA,
      avisos,
      sugerencia,
      estado: r.errores.length ? "error" : avisos.length ? "aviso" : "ok",
    };
  });
};

const money = (n) =>
  Number(n || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });

const badge = {
  ok: { cls: "bg-success", txt: "OK" },
  aviso: { cls: "bg-warning text-dark", txt: "Revisar" },
  error: { cls: "bg-danger", txt: "Error" },
};

// Marca de la agencia: primero la que trae AGENCY_DIRECTORY (campo brand);
// si una agencia nueva no la trae, se deduce del nombre
// ("Nissan Santa Clara" -> "Nissan"); si no hay marca conocida, "all".
const marcaDeAgencia = (agencia, brandOptions = []) => {
  if (agencia?.brand) return agencia.brand;
  const label = agencia?.label || "";
  return brandOptions.find((b) => b && b !== "all" && incluyePalabra(label, b)) || "all";
};

// ---------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------
export default function ImportarCatalogoExcel({
  postJson,
  apiBase,
  agencyOptions = [],
  brandOptions = [],
  tipoInicial = "accesorios",
  onClose,
  onImported,
}) {
  const fileRef = useRef(null);
  const [tipo, setTipo] = useState(tipoInicial);
  const [agId, setAgId] = useState("");
  const [brand, setBrand] = useState("all");
  const [modo, setModo] = useState("reemplazar");
  const [archivo, setArchivo] = useState("");
  const [filas, setFilas] = useState([]);
  const [catalogo, setCatalogo] = useState({ modelos: [], vehiculos: [] });
  const [filtro, setFiltro] = useState("todos");
  const [trabajando, setTrabajando] = useState(false);

  const agencia = agencyOptions.find((a) => a.value === agId);
  const tipoInfo = TIPOS[tipo];

  const marcas = useMemo(() => {
    const deAgencias = agencyOptions.map((a) => a.brand).filter(Boolean);
    return [...new Set(["all", ...brandOptions, ...deAgencias])].filter(Boolean);
  }, [brandOptions, agencyOptions]);

  const resumen = useMemo(() => {
    const r = { ok: 0, aviso: 0, error: 0 };
    filas.forEach((f) => r[f.estado]++);
    return r;
  }, [filas]);

  const filasVisibles = useMemo(
    () => (filtro === "todos" ? filas : filas.filter((f) => f.estado === filtro)),
    [filas, filtro],
  );

  const limpiarTodo = () => {
    setFilas([]);
    setArchivo("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const cargarCatalogo = async (marca) => {
    const data = await postJson(`${apiBase}admin/babilonia/nuevos/list`, {
      q: "",
      marca: marca === "all" ? "" : marca,
      sub_tipo: "all",
      activo: "all",
    });
    return construirCatalogo(Array.isArray(data.data) ? data.data : []);
  };

  const procesarArchivo = async (file) => {
    if (!file) return;
    if (!agId) {
      Swal.fire("Falta agencia", "Selecciona la agencia antes de cargar el Excel.", "warning");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setTrabajando(true);
    try {
      const [{ filas: leidas }, cat] = await Promise.all([leerExcel(file), cargarCatalogo(brand)]);
      if (!leidas.length) throw new Error("El Excel no tiene filas con datos.");

      setCatalogo(cat);
      setFilas(validarFilas(leidas, cat, brand, tipo));
      setArchivo(file.name);
      setFiltro("todos");
    } catch (error) {
      Swal.fire("No se pudo leer el Excel", error.message, "error");
      limpiarTodo();
    } finally {
      setTrabajando(false);
    }
  };

  const payloadBase = () => ({
    type: tipo,
    ag_id: agId,
    agencia_nombre: agencia?.label || agId,
    brand,
    modo,
    rows: filas.map((f) => ({
      fila: f.fila,
      no_pieza: f.no_pieza,
      producto: f.producto,
      aplica_a: f.aplica_a,
      aplica_original: f.aplica_original,
      precio: f.precio,
      tipo_compra: f.tipo_compra,
      image: f.image,
      video: f.video,
      pdf: f.pdf,
    })),
  });

  const importar = async () => {
    if (resumen.error) {
      Swal.fire(
        "Hay filas con error",
        "Corrige el Excel y vuelve a cargarlo. No se guarda nada mientras haya errores.",
        "error",
      );
      return;
    }

    setTrabajando(true);
    try {
      const url = `${apiBase}admin/babilonia/accesorios-garantias/importar`;
      const prueba = await postJson(url, { ...payloadBase(), dry_run: true });
      const r = prueba.resumen || {};

      const listaDesactivar = (prueba.desactivar || [])
        .slice(0, 15)
        .map((d) => `<li>${d.title}</li>`)
        .join("");

      const confirm = await Swal.fire({
        icon: "question",
        title: `Importar ${tipoInfo.label.toLowerCase()} a ${agencia?.label || agId}`,
        html: `
          <div style="text-align:left">
            <p class="mb-1">Marca: <b>${brand === "all" ? "Todas (all)" : brand}</b></p>
            <p><b>${r.insertar ?? 0}</b> nuevos · <b>${r.actualizar ?? 0}</b> actualizados · <b>${r.desactivar ?? 0}</b> desactivados</p>
            ${resumen.aviso ? `<p class="text-warning">${resumen.aviso} fila(s) con aviso; se importan igual.</p>` : ""}
            ${
              r.desactivar
                ? `<p class="mb-1">Se desactivan (no vienen en el Excel):</p><ul style="max-height:160px;overflow:auto;font-size:13px">${listaDesactivar}${
                    r.desactivar > 15 ? `<li>… y ${r.desactivar - 15} más</li>` : ""
                  }</ul>`
                : ""
            }
          </div>`,
        showCancelButton: true,
        confirmButtonText: "Sí, importar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;

      const res = await postJson(url, { ...payloadBase(), dry_run: false });
      const rr = res.resumen || r;
      await Swal.fire(
        "Listo",
        `${rr.insertar} nuevos, ${rr.actualizar} actualizados, ${rr.desactivar} desactivados.`,
        "success",
      );

      onImported?.();
      limpiarTodo();
    } catch (error) {
      Swal.fire("Error al importar", error.message, "error");
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <div className="card mb-3 border-success">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <strong>Importar desde Excel</strong>
          <div className="btn-group btn-group-sm">
            {Object.entries(TIPOS).map(([k, t]) => (
              <button
                key={k}
                type="button"
                disabled={trabajando}
                className={`btn ${tipo === k ? "btn-success" : "btn-outline-success"}`}
                onClick={() => {
                  setTipo(k);
                  limpiarTodo();
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="d-flex gap-2">
          <a className="btn btn-sm btn-outline-success" href={MACHOTES[tipo]} download>
            Descargar machote de {tipoInfo.label.toLowerCase()}
          </a>
          <button type="button" className="btn btn-sm btn-primary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>

      <div className="card-body">
        <div className="row g-2 align-items-end">
          <div className="col-md-4 mb-2">
            <label className="form-label fw-semibold">Agencia</label>
            <select
              className="form-select"
              value={agId}
              disabled={trabajando}
              onChange={(e) => {
                const value = e.target.value;
                setAgId(value);
                setBrand(marcaDeAgencia(agencyOptions.find((a) => a.value === value), brandOptions));
                limpiarTodo();
              }}
            >
              <option value="">Selecciona…</option>
              {agencyOptions
                .filter((a) => a.value)
                .map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
            </select>
          </div>

          <div className="col-md-2 mb-2">
            <label className="form-label fw-semibold">Marca</label>
            <select
              className="form-select"
              value={brand}
              disabled={trabajando}
              onChange={(e) => {
                setBrand(e.target.value);
                limpiarTodo();
              }}
            >
              {marcas.map((b) => (
                <option key={b} value={b}>
                  {b === "all" ? "Todas (all)" : b}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3 mb-2">
            <label className="form-label fw-semibold">Modo</label>
            <select
              className="form-select"
              value={modo}
              disabled={trabajando}
              onChange={(e) => setModo(e.target.value)}
            >
              <option value="reemplazar">Reemplazar lista (desactiva lo que no venga)</option>
              <option value="agregar">Solo agregar / actualizar</option>
            </select>
          </div>

          <div className="col-md-3 mb-2">
            <label className="form-label fw-semibold">Archivo .xlsx</label>
            <input
              ref={fileRef}
              type="file"
              className="form-control"
              accept=".xlsx,.xls"
              disabled={trabajando || !agId}
              onChange={(e) => procesarArchivo(e.target.files?.[0])}
            />
          </div>
        </div>

        <div className="small text-muted">
          Se guardará como <b>{tipoInfo.label.toLowerCase()}</b> de{" "}
          <b>{agencia?.label || "la agencia seleccionada"}</b>, marca{" "}
          <b>{brand === "all" ? "todas (all)" : brand}</b>.
          {modo === "reemplazar" &&
            ` Se desactivan los ${tipoInfo.label.toLowerCase()} de esa agencia y marca que no vengan en el archivo.`}
        </div>

        {trabajando && <div className="text-muted small mt-2">Procesando…</div>}

        {!!filas.length && (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mb-2">
              <span className="text-muted small me-2">
                {archivo} · {filas.length} filas · {catalogo.modelos.length} modelos en catálogo
              </span>
              {[
                ["todos", `Todas (${filas.length})`, "btn-outline-secondary"],
                ["ok", `OK (${resumen.ok})`, "btn-outline-success"],
                ["aviso", `Revisar (${resumen.aviso})`, "btn-outline-warning"],
                ["error", `Error (${resumen.error})`, "btn-outline-danger"],
              ].map(([k, txt, cls]) => (
                <button
                  key={k}
                  type="button"
                  className={`btn btn-sm ${cls} ${filtro === k ? "active" : ""}`}
                  onClick={() => setFiltro(k)}
                >
                  {txt}
                </button>
              ))}

              <div className="ms-auto d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  disabled={trabajando}
                  onClick={limpiarTodo}
                >
                  Limpiar
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  disabled={trabajando || resumen.error > 0}
                  onClick={importar}
                >
                  Importar {filas.length} {tipoInfo.label.toLowerCase()}
                </button>
              </div>
            </div>

            <div className="table-responsive" style={{ maxHeight: 480, overflowY: "auto" }}>
              <table className="table table-sm table-hover align-middle" style={{ fontSize: 13 }}>
                <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th>Fila</th>
                    <th>Estado</th>
                    <th>Imagen</th>
                    <th>{tipo === "garantias" ? "Clave" : "No. pieza"}</th>
                    <th>Título que se guardará</th>
                    <th>Aparece en</th>
                    <th className="text-end">Precio c/IVA</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filasVisibles.map((f) => (
                    <tr key={f.fila}>
                      <td>{f.fila}</td>
                      <td>
                        <span className={`badge ${badge[f.estado].cls}`}>{badge[f.estado].txt}</span>
                      </td>
                      <td>
                        {f.image ? (
                          <a href={f.image} target="_blank" rel="noreferrer">
                            <img
                              src={f.image}
                              alt=""
                              style={{ height: 36, maxWidth: 56, objectFit: "contain" }}
                              onError={(e) => {
                                e.currentTarget.replaceWith(
                                  Object.assign(document.createElement("span"), {
                                    className: "badge bg-danger",
                                    textContent: "No carga",
                                  }),
                                );
                              }}
                            />
                          </a>
                        ) : (
                          <span className="text-muted small">Sin imagen</span>
                        )}
                      </td>
                      <td>{f.no_pieza}</td>
                      <td>
                        {f.title}
                        {f.aplica_original && f.aplica_original !== f.aplica_a && (
                          <div className="text-muted small">Excel: {f.aplica_original}</div>
                        )}
                      </td>
                      <td>{f.aplicaA.length ? f.aplicaA.join(", ") : "—"}</td>
                      <td className="text-end">{money(f.precio)}</td>
                      <td>
                        {[...f.errores, ...f.avisos].map((m, i) => (
                          <div
                            key={i}
                            className={i < f.errores.length ? "text-danger" : "text-warning-emphasis"}
                          >
                            {m}
                          </div>
                        ))}
                        {f.sugerencia && (
                          <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
                            <span className="text-success">
                              Pon en "Aplica a": <b>{f.sugerencia}</b>
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success py-0"
                              onClick={() => navigator.clipboard?.writeText(f.sugerencia)}
                            >
                              Copiar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}